import { ColumnDef, flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import React from 'react';
import styled from 'styled-components';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { HorizontalLayout } from '@/widgets/Layout';
import { PageSpinner } from '@/widgets/Spinner';

import { Text } from '../Text';

interface CondensedTableProps<DataType extends object> {
    columns: ColumnDef<DataType, any>[];
    data: DataType[];
    hideHeader?: boolean;
    sort?: {
        col: string;
        desc: boolean;
    };
    isLoading?: boolean;
    onRowClick?: (row: DataType) => void;
}

export const CondensedTable = <T extends object>({
    data,
    columns,
    hideHeader,
    sort,
    isLoading,
    onRowClick,
}: CondensedTableProps<T>) => {
    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        // This is a hack to enable "auto" size for columns by default as defaultColumn sizes only accept numbers
        defaultColumn: {
            size: 0,
            minSize: 0,
        },
        initialState: {
            sorting: sort ? [{ id: sort.col, desc: sort.desc }] : [],
        },
    });

    return (
        <ErrorBoundary>
            <Table>
                {!hideHeader && (
                    <thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <HeaderCell key={header.id} $width={header.getSize()}>
                                        {header.isPlaceholder ? null : (
                                            <Text helper $uppercase $themeColor="primary">
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                            </Text>
                                        )}
                                    </HeaderCell>
                                ))}
                            </tr>
                        ))}
                    </thead>
                )}

                {!isLoading && (
                    <tbody>
                        {table.getRowModel().rows.map((row) => (
                            <Row
                                key={row.id}
                                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                                $clickable={!!onRowClick}
                            >
                                {row.getVisibleCells().map((cell) => (
                                    <Cell key={cell.id} $width={cell.column.getSize()}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </Cell>
                                ))}
                            </Row>
                        ))}
                    </tbody>
                )}
            </Table>
            {isLoading && (
                <HorizontalLayout $padding="32px" $center $stretch>
                    <PageSpinner />
                </HorizontalLayout>
            )}
        </ErrorBoundary>
    );
};

const Table = styled.table`
    border-radius: 3px;
    width: 100%;
    border-collapse: collapse;
`;

const Row = styled.tr<{ $clickable?: boolean }>`
    &:not(:last-child) {
        border-bottom: 1px solid ${({ theme }) => theme.border.light};
    }
    ${({ $clickable }) =>
        $clickable &&
        `
        cursor: pointer;
        &:hover {
            background-color: rgba(0, 0, 0, 0.03);
        }
    `}
`;

const HeaderCell = styled.th<{ $width?: number }>`
    border-bottom: 1px solid ${({ theme }) => theme.border.light};
    padding: 10px 16px;
    text-align: left;
    width: ${({ $width }) => ($width ? `${$width}px` : 'auto')};
`;

const Cell = styled.td<{ $width?: number }>`
    padding: 10px 16px;
    width: ${({ $width }) => ($width ? `${$width}px` : 'auto')};
`;
